import { useState, useEffect, useRef } from 'react';

export const useProctoring = () => {
  const [metrics, setMetrics] = useState({
    tabSwitches: 0,
    rapidAnswers: 0,
    idleTime: 0,
    questionTimes: [],
    violations: [],
    startTime: Date.now()
  });

  const [warnings, setWarnings] = useState([]);
  const lastActivity = useRef(Date.now());
  const questionStartTime = useRef(Date.now());
  const idleTimer = useRef(null);

  // Track tab visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setMetrics(prev => ({
          ...prev,
          tabSwitches: prev.tabSwitches + 1,
          violations: [...prev.violations, {
            type: 'TAB_SWITCH',
            timestamp: Date.now(),
            severity: 'medium'
          }]
        }));
        
        addWarning('Please stay focused on the assessment');
      } else {
        lastActivity.current = Date.now();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Track user activity for idle detection
  useEffect(() => {
    const updateActivity = () => {
      lastActivity.current = Date.now();
      
      if (idleTimer.current) {
        clearTimeout(idleTimer.current);
      }
      
      idleTimer.current = setTimeout(() => {
        const idleTime = Date.now() - lastActivity.current;
        if (idleTime > 30000) { // 30 seconds idle
          setMetrics(prev => ({
            ...prev,
            idleTime: prev.idleTime + idleTime,
            violations: [...prev.violations, {
              type: 'IDLE_TIME',
              timestamp: Date.now(),
              duration: idleTime,
              severity: 'low'
            }]
          }));
        }
      }, 30000);
    };

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    events.forEach(event => document.addEventListener(event, updateActivity));

    return () => {
      events.forEach(event => document.removeEventListener(event, updateActivity));
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, []);

  const trackQuestionResponse = (questionId, answer) => {
    const responseTime = Date.now() - questionStartTime.current;
    
    setMetrics(prev => {
      const isRapid = responseTime < 1500; // Changed from 3000ms to 1500ms
      return {
        ...prev,
        rapidAnswers: isRapid ? prev.rapidAnswers + 1 : prev.rapidAnswers,
        questionTimes: [...prev.questionTimes, { questionId, responseTime, answer }],
        violations: isRapid ? [...prev.violations, {
          type: 'RAPID_ANSWER',
          timestamp: Date.now(),
          responseTime,
          questionId,
          severity: 'medium'
        }] : prev.violations
      };
    });

    if (responseTime < 1500) { // Changed from 3000ms to 1500ms
      addWarning('Please take time to read questions carefully');
    }

    questionStartTime.current = Date.now();
  };

  const addWarning = (message) => {
    const warning = {
      id: Date.now(),
      message,
      timestamp: Date.now()
    };
    
    setWarnings(prev => [...prev, warning]);
    
    // Auto-remove warning after 5 seconds
    setTimeout(() => {
      setWarnings(prev => prev.filter(w => w.id !== warning.id));
    }, 5000);
  };

  const getIntegrityScore = () => {
    const totalQuestions = metrics.questionTimes.length;
    if (totalQuestions === 0) return 100;

    let score = 100;
    
    // Penalties
    const rapidPenalty = (metrics.rapidAnswers / totalQuestions) * 30;
    const tabSwitchPenalty = Math.min(metrics.tabSwitches * 10, 40);
    const idlePenalty = Math.min(metrics.idleTime / 60000 * 5, 20); // 5 points per minute idle
    
    score -= (rapidPenalty + tabSwitchPenalty + idlePenalty);
    
    return Math.max(0, Math.round(score));
  };

  const getViolationSummary = () => {
    const summary = {
      high: metrics.violations.filter(v => v.severity === 'high').length,
      medium: metrics.violations.filter(v => v.severity === 'medium').length,
      low: metrics.violations.filter(v => v.severity === 'low').length,
      total: metrics.violations.length
    };
    
    return summary;
  };

  return {
    metrics,
    warnings,
    trackQuestionResponse,
    getIntegrityScore,
    getViolationSummary,
    addWarning
  };
};